// src/features/scheduling/components/PersonSearch.tsx
import { useState, useMemo } from "react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Search, User, Mail, Phone, Calendar, Plus, Check, X, Building2, ChevronRight } from "lucide-react";
import type { Person } from "../types";
import { formatUsDate } from "@/utils/date";

interface PersonSearchProps {
  persons: Person[];
  isLoading: boolean;
  selectedPerson: Person | null;
  onSelectPerson: (person: Person | null) => void;
  onAddPerson: () => void;
}

type SearchField = "name" | "email" | "phone" | "dob";

export default function PersonSearch({
  persons,
  isLoading,
  selectedPerson,
  onSelectPerson,
  onAddPerson,
}: PersonSearchProps) {
  const [searchQuery, setSearchQuery] = useState("");
  const [searchField, setSearchField] = useState<SearchField>("name");

  const filteredPersons = useMemo(() => {
    if (!searchQuery.trim()) return persons;

    const query = searchQuery.toLowerCase().trim();

    return persons.filter((person) => {
      switch (searchField) {
        case "name":
          return (
            person.firstName.toLowerCase().includes(query) ||
            person.lastName.toLowerCase().includes(query) ||
            `${person.firstName} ${person.lastName}`.toLowerCase().includes(query)
          );
        case "email":
          return person.email.toLowerCase().includes(query);
        case "phone":
          return person.phone?.replace(/\D/g, "").includes(query.replace(/\D/g, ""));
        case "dob":
          if (!person.dateOfBirth) return false;
          // Accept both YYYY-MM-DD and MM-DD-YYYY search inputs.
          const iso = person.dateOfBirth.slice(0, 10);
          const date = new Date(`${iso}T00:00:00`);
          const us = Number.isNaN(date.getTime()) ? "" : formatUsDate(date);
          return iso.includes(query) || us.toLowerCase().includes(query);
        default:
          return true;
      }
    });
  }, [persons, searchQuery, searchField]);

  const searchFieldOptions: { value: SearchField; label: string; icon: React.ReactNode }[] = [
    { value: "name", label: "Name", icon: <User className="w-4 h-4" /> },
    { value: "email", label: "Email", icon: <Mail className="w-4 h-4" /> },
    { value: "phone", label: "Mobile", icon: <Phone className="w-4 h-4" /> },
    { value: "dob", label: "DOB", icon: <Calendar className="w-4 h-4" /> },
  ];

  const getPlaceholder = () => {
    switch (searchField) {
      case "name":
        return "Search by name...";
      case "email":
        return "Search by email...";
      case "phone":
        return "Search by mobile number...";
      case "dob":
        return "Search by date of birth (MM-DD-YYYY or YYYY-MM-DD)...";
      default:
        return "Search...";
    }
  };

  return (
    <Card className="border-2 border-slate-200 dark:border-slate-700 overflow-hidden">
      <CardHeader className="bg-gradient-to-r from-blue-600 to-indigo-600 text-white pb-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-white/20 rounded-lg backdrop-blur-sm">
              <User className="w-5 h-5" />
            </div>
            <div>
              <CardTitle className="text-lg">Search Visitor</CardTitle>
              <p className="text-sm text-blue-100 mt-0.5">
                Find existing visitors or add new ones
              </p>
            </div>
          </div>
          <Button
            onClick={onAddPerson}
            className="bg-white/20 hover:bg-white/30 text-white border-0 backdrop-blur-sm"
          >
            <Plus className="w-4 h-4 mr-2" />
            Add Visitor
          </Button>
        </div>
      </CardHeader>

      <CardContent className="p-4 space-y-4">
        {/* Search Field Selector */}
        <div className="flex flex-wrap gap-2">
          {searchFieldOptions.map((option) => (
            <Button
              key={option.value}
              variant={searchField === option.value ? "default" : "outline"}
              size="sm"
              onClick={() => {
                setSearchField(option.value);
                setSearchQuery("");
              }}
              className={`flex items-center gap-2 h-9 px-4 rounded-lg transition-all duration-200 ${
                searchField === option.value
                  ? "bg-blue-600 hover:bg-blue-700 text-white shadow-md"
                  : "bg-slate-50 dark:bg-slate-800 text-slate-600 dark:text-slate-300 border-slate-200 dark:border-slate-700"
              }`}
            >
              {option.icon}
              {option.label}
            </Button>
          ))}
        </div>

        {/* Search Input */}
        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate-400" />
          <Input
            placeholder={getPlaceholder()}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10 h-11 border-slate-300 dark:border-slate-600 focus:border-blue-500"
          />
        </div>

        {/* Selected Visitor Display */}
        {selectedPerson && (
          <div className="flex items-center justify-between p-3 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-green-100 dark:bg-green-800 rounded-full">
                <Check className="w-4 h-4 text-green-600 dark:text-green-300" />
              </div>
              <div>
                <p className="font-medium text-green-800 dark:text-green-200">
                  {selectedPerson.firstName} {selectedPerson.lastName}
                </p>
                <p className="text-sm text-green-600 dark:text-green-400">
                  {selectedPerson.email}
                </p>
                {selectedPerson.clientName && (
                  <p className="text-xs text-green-700 dark:text-green-300">
                    Client / Employer: {selectedPerson.clientName}
                  </p>
                )}
              </div>
            </div>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => onSelectPerson(null)}
              className="text-green-700 hover:bg-green-100"
            >
              <X className="w-4 h-4" />
            </Button>
          </div>
        )}

        {/* Visitor List */}
        <div className="max-h-64 overflow-y-auto space-y-2">
          {isLoading ? (
            <div className="space-y-2">
              {[...Array(3)].map((_, i) => (
                <div
                  key={i}
                  className="h-16 bg-slate-100 dark:bg-slate-800 animate-pulse rounded-lg"
                />
              ))}
            </div>
          ) : filteredPersons.length === 0 ? (
            <div className="text-center py-8 text-slate-500">
              <User className="w-12 h-12 mx-auto mb-3 text-slate-300" />
              <p className="font-medium">No visitors found</p>
              <p className="text-sm">Try adjusting your search or add a new visitor</p>
            </div>
          ) : (
            filteredPersons.map((person) => (
              <div
                key={person.id}
                onClick={() => onSelectPerson(person)}
                className={`w-full flex items-center gap-4 p-4 rounded-xl border transition-all duration-300 cursor-pointer group ${
                  selectedPerson?.id === person.id
                    ? "border-blue-500 bg-blue-50/50 dark:bg-blue-900/20 ring-1 ring-blue-500"
                    : "border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 hover:border-blue-400 hover:bg-slate-50/80 dark:hover:bg-slate-800/80 hover:shadow-md"
                }`}
              >
                <div className={`w-12 h-12 rounded-full flex items-center justify-center text-white font-bold text-lg shadow-sm transition-transform group-hover:scale-105 ${
                  selectedPerson?.id === person.id ? "bg-blue-600" : "bg-gradient-to-br from-slate-400 to-slate-500"
                }`}>
                  {person.firstName.charAt(0)}
                  {person.lastName.charAt(0)}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between">
                    <p className={`font-bold text-base truncate ${
                      selectedPerson?.id === person.id ? "text-blue-700 dark:text-blue-300" : "text-slate-900 dark:text-slate-100"
                    }`}>
                      {person.firstName} {person.lastName}
                    </p>
                    <Badge
                      variant={person.status === "ACTIVE" ? "default" : "secondary"}
                      className={
                        person.status === "ACTIVE"
                          ? "bg-emerald-100 text-emerald-700 border-emerald-200 hover:bg-emerald-100"
                          : "bg-slate-100 text-slate-600 border-slate-200 hover:bg-slate-100"
                      }
                    >
                      {person.status}
                    </Badge>
                  </div>
                  <div className="flex flex-wrap items-center gap-x-4 gap-y-1 mt-1">
                    <span className="flex items-center gap-1.5 text-sm text-slate-500 dark:text-slate-400">
                      <Mail className="w-3.5 h-3.5" />
                      {person.email}
                    </span>
                    {person.phone && (
                      <span className="flex items-center gap-1.5 text-sm text-slate-500 dark:text-slate-400">
                        <Phone className="w-3.5 h-3.5" />
                        {person.phone}
                      </span>
                    )}
                  </div>
                  {person.clientName && (
                    <div className="flex items-center gap-1.5 mt-2">
                       <Building2 className="w-3.5 h-3.5 text-slate-400" />
                       <p className="text-xs font-medium text-slate-500 dark:text-slate-400">
                        {person.clientName}
                      </p>
                    </div>
                  )}
                </div>
                {selectedPerson?.id === person.id ? (
                  <div className="bg-blue-600 rounded-full p-1 shadow-sm">
                    <Check className="w-5 h-5 text-white" />
                  </div>
                ) : (
                  <ChevronRight className="w-5 h-5 text-slate-300 group-hover:text-blue-400 transition-colors" />
                )}
              </div>
            ))
          )}
        </div>
      </CardContent>
    </Card>
  );
}
